import axios from 'axios';
import { apiClient } from './client';
import type {
  TicketGarantia, ActaEntrega, FiltrosPostVenta, BitacoraEntry, CrearTicketPayload,
  AsignacionTicket, CrearActaPayload, EstatusTicket,
  Escrituracion, CrearEscrituracionPayload, FiltrosEscrituraciones,
  KpisPostventa, EstatusEscrituracion,
  EncuestaInfo, ResponderEncuestaPayload,
  DiagnosticoTicket, CrearDiagnosticoPayload, AgregarBitacoraPayload,
} from '@/types/postventa';
import type { PaginatedResponse, ApiResponse } from '@/types';

// ─── Encuesta pública (no requiere JWT) ──────────────────────
// La URL incluye tenant para rutas de email-link sin JWT: /encuesta/:tenant/:token
// Misma hermana que `MapaInteractivoTab:52`: sin respaldo, un despliegue sin archivo de
// entorno deja este cliente apuntando a `undefined`. Se arreglan las dos o ninguna.
const publicClient = axios.create({ baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api' });

export const fetchEncuesta = (tenant: string, token: string) =>
  publicClient.get<ApiResponse<EncuestaInfo>>(`/encuesta/${tenant}/${token}`).then(r => r.data.data);

export const responderEncuesta = (tenant: string, token: string, payload: ResponderEncuestaPayload) =>
  publicClient.post<ApiResponse<{ ok: boolean }>>(`/encuesta/${tenant}/${token}`, payload).then(r => r.data.data);

// ─── Tickets de garantía (backend: /postventa/tickets) ───────
export const fetchTickets = (f: FiltrosPostVenta) =>
  apiClient.get<PaginatedResponse<TicketGarantia>>('/postventa/tickets', { params: f }).then(r => r.data);

export const fetchTicket = (id: number) =>
  apiClient.get<ApiResponse<TicketGarantia>>(`/postventa/tickets/${id}`).then(r => r.data.data);

// Borrado 2026-07-29: `actualizarTicket` pedía `PATCH /postventa/tickets/:id`, que no existe
// —el backend solo expone transiciones concretas (`/aperturar`, `/iniciar-diagnostico`,
// `/iniciar-proceso`, `/completar`, `/cancelar`)— y ningún componente la llamaba. El ticket
// no se edita en bloque por diseño: se mueve por su máquina de estados (G02).

// Cerrar = completar ticket con resolucion (anti-self-auth)
export const cerrarTicket = (id: number, resolucion: string) =>
  apiClient.patch<ApiResponse<TicketGarantia>>(`/postventa/tickets/${id}/completar`, { resolucion }).then(r => r.data.data);

// Cancelar ticket (N3+). Backend: PATCH /postventa/tickets/:id/cancelar
// Campo exacto del body: nota_cancelacion (zCancelarTicket). la UI exige `LARGO_MINIMO_MOTIVO_TICKET` (@erp/shared), el mismo del servidor.
export const cancelarTicket = (id: number, nota_cancelacion: string) =>
  apiClient.patch<ApiResponse<{ ticket_id: number; estatus: string }>>(
    `/postventa/tickets/${id}/cancelar`, { nota_cancelacion },
  ).then(r => r.data.data);

// `fetchBitacora` eliminado 2026-07-29: pedía `GET /postventa/tickets/:id/bitacora`, que
// nunca existió como GET. La bitácora viaja embebida en el detalle del ticket.

export const crearTicket = (payload: CrearTicketPayload) =>
  apiClient.post<ApiResponse<TicketGarantia>>('/postventa/tickets', payload).then(r => r.data.data);

// ─── Transiciones de estado (máquina de estado M09) ──────────
// Cada estatus mapea a su propio endpoint de transición
const ESTATUS_ENDPOINT: Partial<Record<EstatusTicket, string>> = {
  APERTURADO:     'aperturar',
  EN_DIAGNOSTICO: 'iniciar-diagnostico',
  EN_PROCESO:     'iniciar-proceso',
};

export const cambiarEstatusTicket = (id: number, estatus: EstatusTicket) => {
  const endpoint = ESTATUS_ENDPOINT[estatus];
  if (!endpoint) {
    return Promise.reject(new Error(`Transición '${estatus}' no soportada desde el cliente`));
  }
  return apiClient.patch<ApiResponse<TicketGarantia>>(`/postventa/tickets/${id}/${endpoint}`, {}).then(r => r.data.data);
};

// ─── Asignaciones ─────────────────────────────────────────────
export const fetchAsignaciones = (ticketId: number) =>
  apiClient.get<ApiResponse<AsignacionTicket[]>>(`/postventa/tickets/${ticketId}/asignaciones`).then(r => r.data.data);

export const asignarProveedor = (ticketId: number, payload: { proveedor_id: number; especialidad: string; descripcion_trabajo: string; costo_estimado?: number }) =>
  apiClient.post<ApiResponse<AsignacionTicket>>(`/postventa/tickets/${ticketId}/asignaciones`, payload).then(r => r.data.data);

// Backend: PATCH /postventa/asignaciones/:id/confirmar (no ticketId en path)
export const confirmarAsignacion = (_ticketId: number, asignacionId: number) =>
  apiClient.patch<ApiResponse<AsignacionTicket>>(`/postventa/asignaciones/${asignacionId}/confirmar`, {}).then(r => r.data.data);

// ─── Actas de entrega (backend: /postventa/actas) ────────────
export const fetchActas = (params: FiltrosPostVenta) =>
  apiClient.get<PaginatedResponse<ActaEntrega>>('/postventa/actas', { params }).then(r => r.data);

export const fetchActa = (id: number) =>
  apiClient.get<ApiResponse<ActaEntrega>>(`/postventa/actas/${id}`).then(r => r.data.data);

export const crearActa = (payload: CrearActaPayload) =>
  apiClient.post<ApiResponse<ActaEntrega>>('/postventa/actas', payload).then(r => r.data.data);

// "Enviar para firma digital" → transición PROGRAMADA → EN_PROCESO (F-3)
// Backend: PATCH /postventa/actas/:id/enviar-firma (sin body)
export const enviarFirmaActa = (id: number) =>
  apiClient.patch<ApiResponse<ActaEntrega>>(`/postventa/actas/${id}/enviar-firma`, {}).then(r => r.data.data);

// Firma efectiva del acta — requiere fecha_firma (el backend la exige)
// Backend: PATCH /postventa/actas/:id/firmar
export const firmarActa = (
  id: number,
  payload: { fecha_firma: string; firma_cliente_url?: string; firma_representante_url?: string; llaves_entregadas?: boolean; numero_copias_llaves?: number; nota_llaves?: string },
) =>
  apiClient.patch<ApiResponse<ActaEntrega>>(`/postventa/actas/${id}/firmar`, payload).then(r => r.data.data);

// ─── Escrituraciones (backend: /postventa/escrituraciones) ───
export const fetchEscrituraciones = (f: FiltrosEscrituraciones) =>
  apiClient.get<PaginatedResponse<Escrituracion>>('/postventa/escrituraciones', { params: f }).then(r => r.data);

export const fetchEscrituracion = (id: number) =>
  apiClient.get<ApiResponse<Escrituracion>>(`/postventa/escrituraciones/${id}`).then(r => r.data.data);

export const crearEscrituracion = (payload: CrearEscrituracionPayload) =>
  apiClient.post<ApiResponse<Escrituracion>>('/postventa/escrituraciones', payload).then(r => r.data.data);

// Backend: PATCH /postventa/escrituraciones/:id (acepta estado+notas en el body)
export const actualizarEstatusEscrituracion = (id: number, estado: EstatusEscrituracion, notas?: string) =>
  apiClient.patch<ApiResponse<Escrituracion>>(`/postventa/escrituraciones/${id}`, { estado, notas }).then(r => r.data.data);

export const actualizarEscrituracion = (id: number, payload: Partial<Escrituracion>) =>
  apiClient.patch<ApiResponse<Escrituracion>>(`/postventa/escrituraciones/${id}`, payload).then(r => r.data.data);

// ─── Diagnóstico (M09-10/11/12) ──────────────────────────────
export const fetchDiagnostico = (ticketId: number) =>
  apiClient.get<ApiResponse<DiagnosticoTicket[]>>(`/postventa/tickets/${ticketId}/diagnostico`).then(r => r.data.data);

export const crearDiagnostico = (ticketId: number, payload: CrearDiagnosticoPayload) =>
  apiClient.post<ApiResponse<DiagnosticoTicket>>(`/postventa/tickets/${ticketId}/diagnostico`, payload).then(r => r.data.data);

export const finalizarDiagnostico = (diagnosticoId: number) =>
  apiClient.patch<ApiResponse<DiagnosticoTicket>>(`/postventa/diagnostico/${diagnosticoId}/finalizar`, {}).then(r => r.data.data);

// ─── Asignaciones completar/cancelar (M09-16/17) ─────────────
export const completarAsignacion = (asignacionId: number, payload: { costo_real?: number; observaciones?: string }) =>
  apiClient.patch<ApiResponse<AsignacionTicket>>(`/postventa/asignaciones/${asignacionId}/completar`, payload).then(r => r.data.data);

export const cancelarAsignacion = (asignacionId: number, motivo: string) =>
  apiClient.patch<ApiResponse<AsignacionTicket>>(`/postventa/asignaciones/${asignacionId}/cancelar`, { motivo }).then(r => r.data.data);

// ─── Bitácora POST (M09-09) ───────────────────────────────────
export const agregarBitacora = (ticketId: number, payload: AgregarBitacoraPayload) =>
  apiClient.post<ApiResponse<BitacoraEntry>>(`/postventa/tickets/${ticketId}/bitacora`, payload).then(r => r.data.data);

// ─── KPIs Postventa ──────────────────────────────────────────
export const fetchKpisPostventa = () =>
  apiClient.get<ApiResponse<KpisPostventa>>('/postventa/dashboard').then(r => r.data.data);
